import React, { useContext, useEffect, useState } from "react";
import PropTypes from 'prop-types';

import Grid from '@mui/material/Unstable_Grid2';
import { Box, Card, CardHeader, CardContent, Container, Stack, Tooltip, Typography } from '@mui/material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import WidgetSummary from 'components/widgets/widget-summary';
import { PrivateRoutesContext } from "app/PrivateRoutesContext";

import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import MouseIcon from '@mui/icons-material/Mouse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import AximDoughnut from "components/Content/Graphs/AximDoughnut";
import { apiGet } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";


const additionalData = [
    { title: "Total", total: () => { return "100"; }, icon: <MouseIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
    { title: "Total", total: () => { return "100"; }, icon: <MouseIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
]
const sessionAttendanceData = [
    { title: "Session 1", total: () => { return "75"; }, icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
    { title: "Session 2", total: () => { return "18"; }, icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
    { title: "Session 3", total: () => { return "25"; }, icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
    { title: "Session 4", total: () => { return "53"; }, icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
    { title: "Session 5", total: () => { return "46"; }, icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
]
function widget(props, idx) {
    const { title, total, icon, tooltip } = props;
    return (
        <Tooltip title={tooltip} arrow placement="top" key={`widget-item-${idx}`} >
            <Grid xs={12} sm={12} md={6} lg={4} xl={3}>
                <WidgetSummary title={title} total={total()} color="success" icon={icon} />
            </Grid>
        </Tooltip>
    );
}

widget.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    total: PropTypes.func.isRequired,
    icon: PropTypes.node
};

function donut(props, idx) {
    const { title, showStack, showTotal, data, isCurrency } = props;

    return (
        <Grid xs={12} md={2} key={`donut-item-${idx}`} >
            <AximDoughnut
                title={title}
                showStack={showStack ?? false}
                showTotal={showTotal ?? false}
                data={data}
                isCurrency={isCurrency ?? false}
            />
        </Grid>
    );
}

donut.propTypes = {
    title: PropTypes.string.isRequired,
    showStack: PropTypes.bool,
    showTotal: PropTypes.bool,
    data: PropTypes.array.isRequired,
    isCurrency: PropTypes.bool
};

function DataSection(props) {
    const { children, title, sx, ...other } = props;
    return (
        <Box sx={{ pb: 5, ...sx, }} {...other}>
            <Accordion >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-${title}`}
                    id={`panel-${title}-header`}
                >
                    <Grid container xs="12">
                        <Typography variant="h4" sx={{ mb: 1 }}>{title} </Typography>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2} xs="12" >
                        {children}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}

DataSection.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string.isRequired,
};


TableList.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    subheader: PropTypes.string,
};

function TableList(props) {
    const { data, title, subheader, ...otherProps } = props;
    return (
        <Grid xs={12} sm={3} >
            <Card >
                <CardHeader title={title} subheader={subheader} />
                <CardContent>
                    <Stack spacing={1} >
                        {data.map((e, i) =>
                            <Card key={i} className="d-flex p-1" style={{ justifyContent: "space-between" }}>
                                <Stack direction="row" spacing={1}>
                                    <Box>{e.label}</Box>
                                </Stack>
                                <Box>{e.value}</Box>
                            </Card>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Grid>
    );
}

export default function ShowcaseDashboard({ isVisible }) {
    const { companyId } = useContext(PrivateRoutesContext);
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [liveEventData, setLiveEventData] = useState(null);
    const [educationData, setEducationData] = useState(null);
    const [navigationData, setNavigationData] = useState(null);
    const [communicationEngagementData, setCommunicationEngagementData] = useState(null);
    
    useEffect(() => {
        if (isVisible) {
            setIsLoading(true);
            apiGet(apiUrls.getShowcaseDashboard(companyId))
                .then((response) => {
                    if (response.data) {
                        updateUserData(response.data.userDashboard);
                        updateLiveEvent(response.data.liveEventDashboard);
                        updateEducationData(response.data.educationDashboard);
                        updateNavigationData(response.data.navigationDashboard);
                        updateCommunicationData(response.data.communicationEngagementDashboard);
                    }
                })
                .catch((error) => {
                    
                })
                .finally(() => {
                    setIsLoading(false);
                })
        }
    }, [isVisible, companyId]);

    const updateUserData = (u) => {
        if (u) {
            var tempUserData = {
                userCounts: [
                    {
                        title: "Total Users Invited",
                        tooltip: "The total number of invited users to your showcase.",
                        total: () => { return u.totalUsersInvited; },
                        icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} />
                    },
                    {
                        title: "Total Users Accepted",
                        tooltip: "The total number of users that accepted their invites.",
                        total: () => { return u.totalUsersAccepted; },
                        icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} />
                    }
                ],
                mobileCounts: [
                    {
                        title: "Mobile vs Desktop",
                        data: [
                            { label: "Mobile", value: u.mobileUserCount },
                            { label: "Desktop", value: u.desktopUserCount },
                        ],
                    },
                    {
                        title: "Mobile Push Enabled",
                        data: [
                            { label: "Yes", value: u.mobileUserCount - u.mobileUserPushEnabledCount },
                            { label: "No", value: u.mobileUserPushEnabledCount },
                        ],
                    }
                ]
            };

            setUserData(tempUserData);
        }
    }

    const updateLiveEvent = (l) => {
        if (l) {
            var tempLiveEvents = {
                liveEventCounts: [
                        {
                        title: "Live Event Attendance",
                        tooltip: "This is the number of users who have logged in during your most recent live event",
                        total: () => { return l.liveEventAttendanceCount; },
                        icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} />
                    },
                    {
                        title: "Live Event Attendance %",
                        tooltip: "This is the percentage of active users who logged in during a live event",
                        total: () => { return `${l.liveEventAttendancePercentage}%`; },
                        icon: <PeopleOutlinedIcon sx={{ color: "primary.main", fontSize: 60 }} />
                    }
                ],
                liveEventLists: [
                    {
                        title: "Top 3 Sessions by RSVP",
                        data: l.sessions.map(s => {
                            return {
                                label: s.title,
                                value: s.attendees,
                            };
                        })
                    },
                    {
                        title: "Top 3 Searches during Live Event",
                        data: l.searches.map(s => {
                            return {
                                label: s.term,
                                value: s.count,
                            }
                        })
                    }
                ],
            };

            setLiveEventData(tempLiveEvents);
        }
    }

    const updateEducationData = (e) => {
        if (e) {
            var tempEducationData = {
                educationCounts: [
                    { title: "Total Profile Views", total: () => { return e.totalProfileViews; }, icon: <VisibilityIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
                    { title: "Total Profile Follows", total: () => { return e.totalProfileFollows; }, icon: <BookmarkAddedIcon sx={{ color: "primary.main", fontSize: 60 }} /> },
                ],
                educationLists: [],
            };

            if (e.popularBenefits) {
                tempEducationData.educationLists.push({
                        title: "Top 3 Most Popular Benefits",
                        data: e.popularBenefits.map(s => {
                            return {
                                label: s.benefitName,
                                value: s.count,
                            }
                        })
                    })
            }

            if (e.unpopularBenefits) {
                tempEducationData.educationLists.push({
                    title: "3 Least Popular Benefits",
                    data: e.unpopularBenefits.map(s => {
                        return {
                            label: s.benefitName,
                            value: s.count,
                        }
                    })
                });
            }

            if (e.mostWatchedVideos) {
                tempEducationData.educationLists.push({
                    title: "Top 3 Most Watched Videos",
                    data: e.mostWatchedVideos.map(v => {
                        return {
                            label: v,
                        }
                    })
                });
            }

            setEducationData(tempEducationData);
        }
    }

    const updateNavigationData = (n) => {
        if (n) {
            var tempNavigationData = {
                commonSearchTerms: n.commonSearchTerms.map(t => {
                    return { label: t.term, value: t.count, }
                }),
                info: [
                    { title: "Total Clicks", total: () => { return n.totalClicks; }, icon: <MouseIcon sx={{ color: "primary.main", fontSize: 60 }} /> }
                ],
                profiles: []
            };

            if (n.mostNavigatedProfiles) {
                tempNavigationData.profiles.push({
                    title: "Top 3 Most Navigated Profiles",
                    data: n.mostNavigatedProfiles.map(p => {
                        return { label: p.profileName, value: p.clicks }
                    })
                });
            }
            
            if (n.leastNavigatedProfiles) {
                tempNavigationData.profiles.push({
                    title: "3 Least Navigated Profiles",
                    data: n.leastNavigatedProfiles.map(p => {
                        return { label: p.profileName, value: p.clicks }
                    })
                });
            }

            setNavigationData(tempNavigationData);
        }
    }

    const updateCommunicationData = (c) => {
        if (c) {
            var tempCommunicationData = {
                notifications: [],
            };

            if (c.communicationTypes) {
                tempCommunicationData.notifications.push({
                    title: "Communication Type Enrollments",
                    data: c.communicationTypes.map(t => {
                        return {
                            label: t.communicationType,
                            value: t.count,
                        }
                    })
                });
            }
            
            if (c.notificationStats) {
                tempCommunicationData.notifications.push({
                    title: "Sent Notifications by Type",
                    data: c.notificationStats.map(n => {
                        return {
                            label: n.notificationType,
                            value: n.count,
                        }
                    })
                });
            }

            setCommunicationEngagementData(tempCommunicationData);
        }
    }

    return (
        <Container maxWidth="xl">
            {userData ?
                <DataSection title="Users" >
                    {userData.userCounts.map((val, i) => {
                        return (widget(val, i));
                    })}
                    {userData.mobileCounts.map((val, i) => {
                        return (donut(val, i));
                    })}
                </DataSection> : null}

            {liveEventData ?
                <DataSection title="Live Events" >
                    {liveEventData.liveEventCounts.map((val, i) => {
                        return (widget(val, i)
                        );
                    })}
                    {liveEventData.liveEventLists.map((val, i) => 
                        <TableList key={i}
                            title={val.title}
                            data={val.data}
                        />
                    )}
                </DataSection> : null}

            {educationData ?
                <DataSection title="Education Engagement" >
                    {educationData.educationCounts.map((val, i) => {
                        return (widget(val, i)
                        );
                    })}
                    {educationData.educationLists.map((val, i) =>
                        <TableList key={i}
                            title={val.title}
                            data={val.data}
                        />
                    )}
                </DataSection> : null}
            {navigationData ?
                <DataSection title="Navigation Engagement" >
                    <TableList
                        title="Top 5 Most Common Search Terms"
                        data={navigationData.commonSearchTerms}
                    />
                    {navigationData.info.map((val, i) => {
                        return (widget(val, i)
                        );
                    })}
                    {navigationData.profiles.map((val, i) => 
                        <TableList key={i}
                            title={val.title}
                            data={val.data}
                        />
                    )}
                </DataSection> : null}

            {communicationEngagementData ?
                <DataSection title="Communication Engagement" >
                    {communicationEngagementData.notifications.map((val, i) => {
                        return (donut(val, i)
                        );
                    })}
                </DataSection> : null}

            {/* <DataSection hidden={true} title="Additional Engagement" >
                {additionalData.map((val, i) => {
                    return (widget(val, i)
                    );
                })}
            </DataSection> */}

            {/* <DataSection title="Life Changes & Interest" >
                {liveEventData.map((val, i) => {
                    return (widget(val, i)
                    );
                })}

                <TableList
                    title="Life Event Breakdown"
                    data={[
                        { label: "Marriage", value: "10%" },
                        { label: "Retirement planning", value: "9%" },
                        { label: "Birth", value: "5%" },
                        { label: "Spouse or dependent loss of other health insurance coverage", value: "5%" },
                        { label: "Divorce", value: "4%" },
                        { label: "Adoption", value: "3%" },
                        { label: "Death", value: "3%" },
                        { label: "Gain or loss of legal custody of a dependent", value: "2%" },
                    ]}
                />
            </DataSection>
            <DataSection title="Cost & Effect" >
                {liveEventData.map((val, i) => {
                    return (widget(val, i)
                    );
                })}
            </DataSection> */}
        </Container>
    );
}
